import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { OATBIZ_HOST } from '@/services/constants'
import { useToastStore } from '@/plugins/toast'
import { messageFromError } from '@/utils'

export const useReferralStore = defineStore('referral', () => {
  const toast = useToastStore()

  const code = ref<string>()
  const campaign = ref<string>()
  const isValidating = ref(false)
  const INVALID_MESSAGE = 'Sorry, that referral code is invalid.'

  const hasReferral = computed(() => code.value?.length ? true : false)

  const validateCode = async (inputCode: string): Promise<string | null> => {
    isValidating.value = true
    try {
      const response = await fetch(`${OATBIZ_HOST}/api/referrals/validate-code?code=${inputCode}`)
      const data = await response.json()
      if (!data?.code) {
        throw new Error(data?.message || INVALID_MESSAGE)
      }
      return data.code
    } catch (error) {
      console.warn('[referral] Could not validate code', error)
      const message = messageFromError(error, INVALID_MESSAGE)
      toast.alert({ message, type: 'error', duration: 5000 })
      return null
    } finally {
      isValidating.value = false
    }
  }

  const setReferralCode = async (inputCode: string) => {
    const validatedCode = await validateCode(inputCode)
    if (validatedCode) {
      code.value = validatedCode
      toast.alert({ message: 'Referral Discount Applied!', type: 'notice', duration: 5000 })
    } else {
      reset()
    }
  }

  const init = async () => {
    const urlParams = new URLSearchParams(window.location.search)
    const param = (name: string) => String(urlParams.get(name)).toUpperCase()
    const utmSource = param('utm_source')
    const utmCampaign = param('utm_campaign')

    if (utmSource === 'REFERRAL' && utmCampaign !== campaign.value) {
      campaign.value = utmCampaign
      await setReferralCode(utmCampaign)
    }
  }

  const reset = () => {
    code.value = campaign.value = undefined
  }

  return {
    code,
    campaign,
    isValidating,
    hasReferral,
    init
  }
}, {
  persist: {
    storage: window.sessionStorage,
    pick: [ 'code', 'campaign' ],
    afterHydrate: ({ store }) => {
      store.init()
      store.$persist()
    }
  }
})

